import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Collapse, Typography } from "antd";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import { openPopup2, POPUP_TYPE } from "../../../contexts/PopupContext";
import { useTrackingOp } from "../../../contexts/TrackingOpContext";
import { loginActions } from "../../../pages/LoginPage/reducer";
import { VesselSideService } from '../../../util/services/vessel-side';
import { showErrorResponse } from "../../../util/varibles/global";
import { IChecklist, IOperation2 } from "../../../util/varibles/interface";
import Checklist from "../../Checklist";
import Footer from "../../Popup/_components/Footer";
import styles from "./style.module.scss";

interface IProps {
    data: IOperation2
    activeIds?: string[]
    checklists: IChecklist[]

    isChangeStep?: boolean
    onClose?(): void
}

const ChecklistsForm: React.FC<IProps> = ({ data, activeIds = [], checklists = [], isChangeStep = true, onClose }) => {
    const dispatch = useDispatch();
    const valueRef = useRef<{ [id: string]: any }>({});
    const [loading, setLoading] = useState(false);
    const vesselTenantIdRef = useRef(data.vessel?.tenant_id);

    const handleSubmit = useCallback(() => {
        setLoading(true);

        const { id, tenant_id, vessel_id, checklists_v2 = [] } = data.operation;
        const checklists = checklists_v2.map(item => {
            const { [item.id || ""]: result } = valueRef.current;
            if (result)
                return { ...item, result: JSON.stringify(result) }
            return item;
        });

        return isChangeStep
            ? VesselSideService.updateState({
                operation_id: id,
                tenant_id,
                vessel_id,
                checklists_v2: checklists
            })
                .then(({ operation, activity_log }) => {
                    const operationNew = { ...data, activity_log, operation: { ...data.operation, ...operation } };
                    dispatch(loginActions.updateOpOfVessel(operationNew as IOperation2));
                    onClose?.();
                })
                .catch(err => showErrorResponse('Update checklists failure', err))
                .finally(() => setLoading(false))
            : VesselSideService.updateChecklist({
                operation_id: id,
                tenant_id,
                checklists_v2: checklists
            })
                .then((checklists_v2) => {
                    const operationNew = { ...data, operation: { ...data.operation, checklists_v2 } };
                    dispatch(loginActions.updateOpOfVessel(operationNew));
                    onClose?.();
                })
                .catch(err => showErrorResponse('Update checklists failure', err))
                .finally(() => setLoading(false))

    }, [data, isChangeStep, dispatch, onClose]);

    const handleChange = (id: string, value: any) => {
        valueRef.current = { ...valueRef.current, [id]: value }
    }

    const items = useMemo(() => checklists.map((item) => {
        const { name = '' } = item || {};

        return {
            key: item.id,
            label: name,
            children: <Checklist
                checklist={item}
                vesselTenantId={vesselTenantIdRef.current}
                onResultChange={value => handleChange(item.id || "", value)}
            />
        }
    }), [checklists]);

    return <div>
        <Collapse
            className={`${styles.checklistsForm} max-w-[860px] mx-auto`}
            bordered={false}
            expandIconPlacement='end'
            defaultActiveKey={activeIds}
            expandIcon={props => props.isActive ?
                <MinusCircleOutlined style={{ fontSize: '18px' }} /> :
                <PlusCircleOutlined style={{ fontSize: '18px' }} />
            }
            items={items}
        />
        <Footer
            className='max-w-[860px] mx-auto'
            ok={{ click: handleSubmit, text: 'Save', loading }}
            cancel={{ click: onClose }}
        />
    </div>
};

export const ChecklistsSection = ({ data = [] }: { data?: IChecklist[] }) => {
    const [op] = useTrackingOp(state => state.op);

    const handleClick = useCallback((id: string = "") => {
        if (op)
            openPopup2(<ChecklistsForm
                data={op}
                activeIds={[id]}
                checklists={data}
                isChangeStep={false}
            />, { modalProps: { title: "Checklists", width: '100%' }, popupType: POPUP_TYPE.DRAWER })
    }, [data, op])

    return data.length > 0 ? <>
        <Typography.Title level={4} className="!mb-0 mt-5">Remaining checklists</Typography.Title>
        <div className="flex flex-col mt-2 gap-1">
            {data?.map(item => <div key={item.id} className="link" onClick={() => handleClick(item.id)}>{item.name}</div>)}
        </div>
    </> : null
}

export default ChecklistsForm;
